import React from 'react';
import { Progress } from '../ui/Progress';
import { Button } from '../ui/Button';
import { Skeleton } from '../ui/Skeleton';
import { 
  Zap, 
  MessageSquare, 
  BarChart3, 
  Users,
  ArrowRight
} from 'lucide-react';

interface UsageMetrics {
  apiCalls: { used: number; limit: number };
  aiTokens: { used: number; limit: number };
  projects: { used: number; limit: number };
  seats: { used: number; limit: number };
}

interface UsageOverviewProps {
  usage?: UsageMetrics;
  isLoading?: boolean;
}

export function UsageOverview({ usage, isLoading = false }: UsageOverviewProps) {
  const defaultUsage: UsageMetrics = {
    apiCalls: { used: 0, limit: 2000 },
    aiTokens: { used: 0, limit: 20000 },
    projects: { used: 0, limit: 3 },
    seats: { used: 0, limit: 3 }
  };

  const currentUsage = usage || defaultUsage;

  const usageItems = [
    {
      label: 'API Calls',
      icon: BarChart3,
      color: 'blue',
      data: currentUsage.apiCalls
    },
    {
      label: 'AI Tokens',
      icon: MessageSquare,
      color: 'purple',
      data: currentUsage.aiTokens
    },
    {
      label: 'Projects',
      icon: Zap,
      color: 'green',
      data: currentUsage.projects
    },
    {
      label: 'Team Seats',
      icon: Users,
      color: 'pink',
      data: currentUsage.seats
    }
  ];

  if (isLoading) {
    return (
      <div className="space-y-6">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="space-y-2">
            <div className="flex items-center justify-between">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-20" />
            </div>
            <Skeleton className="h-2 w-full" />
            <div className="flex justify-between">
              <Skeleton className="h-3 w-12" />
              <Skeleton className="h-3 w-16" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {usageItems.map((item, index) => {
        const percentage = Math.min(Math.round((item.data.used / item.data.limit) * 100), 100);
        
        return (
          <div key={index} className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <item.icon className="w-4 h-4 text-gray-600 dark:text-gray-400 mr-2" />
                <span className="text-sm font-medium text-gray-900 dark:text-white">{item.label}</span>
              </div>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {item.data.used.toLocaleString()} / {item.data.limit.toLocaleString()}
              </span>
            </div>
            <Progress value={percentage} className="h-2" />
            <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
              <span>{percentage.toFixed(1)}% used</span>
              <span>{(item.data.limit - item.data.used).toLocaleString()} remaining</span>
            </div>
          </div>
        );
      })}
      
      <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
        <Button variant="outline" size="sm" className="w-full" onClick={() => window.location.href = '/billing'}>
          View detailed usage
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}